<template>
  <v-menu
    top
    offset-y
    transition="fab-transition"
    origin="bottom left"
    open-on-hover
    :close-delay="1000"
    v-model="board"
    :nudge-left="40"
    content-class="reaction-menu"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        v-bind="attrs"
        v-on="on"
        text
        :color="getColor"
        @click="onClickMainButton"
        @mousedown="openMenu"
        class="reaction-button"
        :class="{ 'active-reaction': liked }"
      >
        <div class="mr-3 reaction-icon-container">
          <v-img
            :src="require(`@/assets/svg/reactions/core/${getIcon}.svg`)"
            width="30"
            height="30"
            contain
            class="reaction-icon"
            :class="`reaction-${getIcon}`"
          />
        </div>
        {{ liked ? liked : "Like" }}
        <!-- <span v-if="showCounter" class="ml-1 reaction-counter">{{ counter }}</span> -->
      </v-btn>
    </template>
    <v-card class="mb-3 reaction-picker rounded-pill">
      <v-tooltip
        top
        v-for="button in buttons"
        :key="button.type"
        transition="fade-transition"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            fab
            dark
            :small="$vuetify.breakpoint.mdAndUp"
            :x-small="$vuetify.breakpoint.smAndDown"
            :color="button.color"
            :class="
              `reaction-option ${
                $vuetify.breakpoint.smAndDown ? 'ma-1' : 'ma-3'
              }`
            "
            v-bind="attrs"
            v-on="on"
            @click="onLike(button.type)"
            @mouseenter="playHoverAnimation(button.type)"
          >
            <div class="reaction-emoji">
              <v-img
                :src="
                  require(`@/assets/svg/reactions/circle/${button.type.toLowerCase()}.svg`)
                "
                :width="$vuetify.breakpoint.mdAndUp ? 25 : 30"
                :height="$vuetify.breakpoint.mdAndUp ? 25 : 30"
                contain
                :class="`reaction-${button.type.toLowerCase()}`"
              />
            </div>
            <div class="reaction-tooltip">{{ button.type }}</div>
          </v-btn>
        </template>
        {{ button.type }}
      </v-tooltip>
    </v-card>
  </v-menu>
</template>

<script>
export default {
  props: {
    onLike: Function,
    liked: {
      type: String,
      default: ""
    },
    counter: {
      type: Number,
      default: 0
    },
    showCounter: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      buttons: [
        {
          type: "Like",
          color: "#1485BD",
          animation: "bounce"
        },
        {
          type: "Celebrate",
          color: "#6dae4f",
          animation: "tada"
        },
        {
          type: "Support",
          color: "#B094D1",
          animation: "pulse"
        },
        {
          type: "Love",
          color: "#DF704D",
          animation: "heartbeat"
        },
        {
          type: "Insightful",
          color: "#FEBB5C",
          animation: "wobble"
        },
        {
          type: "Curious",
          color: "#DEA2DB",
          animation: "swing"
        }
      ],
      board: false,
      currentHover: null,
      hoverTimeout: null
    };
  },
  methods: {
    onClickMainButton() {
      this.board = false;
      this.onLike(this.liked ? this.liked : "Like");
      this.playClickAnimation();
    },
    openMenu() {
      if (!this.board) this.board = true;
    },
    playHoverAnimation(type) {
      this.currentHover = type;

      // Clear any existing timeout
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout);
      }

      // Play animation after a small delay
      this.hoverTimeout = setTimeout(() => {
        const emoji = document.querySelector(`.reaction-${type.toLowerCase()}`);
        if (emoji) {
          emoji.classList.add("animate");
          setTimeout(() => {
            emoji.classList.remove("animate");
          }, 1000);
        }
      }, 100);
    },
    playClickAnimation() {
      if (this.liked) {
        const icon = document.querySelector(`.reaction-${this.getIcon}`);
        if (icon) {
          icon.classList.add("click-animate");
          setTimeout(() => {
            icon.classList.remove("click-animate");
          }, 1000);
        }
      }
    }
  },
  computed: {
    getColor() {
      if (!this.liked) return "grey";
      const button = this.buttons.find(b => b.type === this.liked);
      return button ? button.color : "grey";
    },
    getIcon() {
      if (!this.liked) return "empty";
      return this.liked.toLowerCase();
    }
  }
};
</script>

<style scoped>
/* Base Styles */
.reaction-button {
  position: relative;
  transition: all 0.3s ease;
  text-transform: none;
  letter-spacing: normal;
  font-weight: 500;
  padding: 0 12px;
}

.reaction-button:hover {
  transform: translateY(-2px);
}

.active-reaction {
  font-weight: 600;
}

.reaction-icon-container {
  position: relative;
  transition: all 0.3s ease;
}

.reaction-counter {
  font-size: 0.85rem;
  color: #65676b;
}

/* Menu Styles */
.reaction-menu {
  box-shadow: none !important;
  padding: 0 10px;
}

.reaction-picker {
  background: white !important;
  padding: 8px 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  display: flex;
  align-items: center;
  justify-content: center;
}

.reaction-option {
  transition: all 0.2s ease;
  position: relative;
  overflow: visible !important;
}

.reaction-option:hover {
  transform: scale(1.2) translateY(-10px) !important;
  z-index: 10;
}

.reaction-tooltip {
  position: absolute;
  top: -35px;
  left: 50%;
  transform: translateX(-50%);
  background: #333;
  color: white;
  padding: 4px 8px;
  border-radius: 8px;
  font-size: 12px;
  opacity: 0;
  pointer-events: none;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.reaction-option:hover .reaction-tooltip {
  opacity: 1;
  top: -40px;
}

/* Animation Classes */
.reaction-emoji {
  transition: all 0.3s ease;
}

.reaction-emoji.animate {
  animation: bounce 0.6s ease;
}

.reaction-like.animate {
  animation: bounce 0.6s ease;
}

.reaction-celebrate.animate {
  animation: tada 0.8s ease;
}

.reaction-support.animate {
  animation: pulse 0.8s ease;
}

.reaction-love.animate {
  animation: heartbeat 0.8s ease;
}

.reaction-insightful.animate {
  animation: wobble 0.8s ease;
}

.reaction-curious.animate {
  animation: swing 0.8s ease;
}

.reaction-icon.click-animate {
  animation: pop 0.4s ease;
}

/* Keyframe Animations */
@keyframes bounce {
  0%,
  100% {
    transform: scale(1);
  }
  25% {
    transform: scale(1.2);
  }
  50% {
    transform: scale(0.9);
  }
  75% {
    transform: scale(1.1);
  }
}

@keyframes tada {
  0% {
    transform: scale(1);
  }
  10%,
  20% {
    transform: scale(0.9) rotate(-5deg);
  }
  30%,
  50%,
  70%,
  90% {
    transform: scale(1.1) rotate(5deg);
  }
  40%,
  60%,
  80% {
    transform: scale(1.1) rotate(-5deg);
  }
  100% {
    transform: scale(1) rotate(0);
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes heartbeat {
  0% {
    transform: scale(1);
  }
  14% {
    transform: scale(1.3);
  }
  28% {
    transform: scale(1);
  }
  42% {
    transform: scale(1.3);
  }
  70% {
    transform: scale(1);
  }
}

@keyframes wobble {
  0% {
    transform: translateX(0%);
  }
  15% {
    transform: translateX(-25%) rotate(-5deg);
  }
  30% {
    transform: translateX(20%) rotate(3deg);
  }
  45% {
    transform: translateX(-15%) rotate(-3deg);
  }
  60% {
    transform: translateX(10%) rotate(2deg);
  }
  75% {
    transform: translateX(-5%) rotate(-1deg);
  }
  100% {
    transform: translateX(0%);
  }
}

@keyframes swing {
  20% {
    transform: rotate(15deg);
  }
  40% {
    transform: rotate(-10deg);
  }
  60% {
    transform: rotate(5deg);
  }
  80% {
    transform: rotate(-5deg);
  }
  100% {
    transform: rotate(0deg);
  }
}

@keyframes pop {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.3);
  }
  100% {
    transform: scale(1);
  }
}

/* Responsive Adjustments */
@media (max-width: 600px) {
  .reaction-picker {
    padding: 6px 8px;
  }

  .reaction-option {
    margin: 2px !important;
  }

  .reaction-button {
    padding: 0 8px;
  }
}
</style>
